import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { useAdminAuth } from './AdminLayout';
import { toast } from 'sonner';
import { Gift, Save, Loader2, Utensils, Clock, DollarSign, Percent, ShieldCheck } from 'lucide-react';

export default function AdminSettings() {
  const { password } = useAdminAuth();
  const client = adminApi(password);
  
  const [activeTab, setActiveTab] = useState('system'); // 'coupons' | 'system'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Welcome coupon states
  const [enabled, setEnabled] = useState(false);
  const [code, setCode] = useState('');
  const [coupons, setCoupons] = useState([]);

  // System config states
  const [deliveryFee, setDeliveryFee] = useState(40);
  const [minOrderValue, setMinOrderValue] = useState(0);
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState(500);
  const [taxPercentage, setTaxPercentage] = useState(5);
  const [showTaxAndDelivery, setShowTaxAndDelivery] = useState(true);
  const [isOnline, setIsOnline] = useState(true);
  const [allowPreorders, setAllowPreorders] = useState(true);
  const [operatingHours, setOperatingHours] = useState({});
  const [acceptCod, setAcceptCod] = useState(true);
  const [acceptCashfree, setAcceptCashfree] = useState(true);
  const [enableTodaysSpecial, setEnableTodaysSpecial] = useState(true);
  const [todaysSpecialTitle, setTodaysSpecialTitle] = useState("Today's Special");

  const daysOrdered = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: c }, { data: sys }] = await Promise.all([
          client.get('/admin/settings/welcome-coupon'),
          client.get('/admin/coupons'),
          client.get('/admin/settings/system'),
        ]);
        
        setEnabled(s.enabled);
        setCode(s.code || '');
        setCoupons(c);

        setDeliveryFee(sys.delivery_fee ?? 40);
        setMinOrderValue(sys.min_order_value ?? 0);
        setFreeDeliveryThreshold(sys.free_delivery_threshold ?? 500);
        setTaxPercentage(sys.tax_percentage ?? 5);
        setShowTaxAndDelivery(sys.show_tax_and_delivery ?? true);
        setIsOnline(sys.is_online ?? true);
        setAllowPreorders(sys.allow_preorders ?? true);
        setOperatingHours(sys.operating_hours || {});
        setAcceptCod(sys.accept_cod ?? true);
        setAcceptCashfree(sys.accept_cashfree ?? true);
        setEnableTodaysSpecial(sys.enable_todays_special ?? true);
        setTodaysSpecialTitle(sys.todays_special_title || "Today's Special");
      } catch {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const getErrorMessage = (e) => {
    const detail = e.response?.data?.detail;
    if (!detail) return 'Save failed';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(err => {
        const field = err.loc ? err.loc.join('.') : '';
        return `${field ? field + ': ' : ''}${err.msg}`;
      }).join(', ');
    }
    return JSON.stringify(detail);
  };

  const saveWelcome = async () => {
    if (enabled && !code) return toast.error('Pick a coupon code to auto-apply');
    setSaving(true);
    try {
      await client.put('/admin/settings/welcome-coupon', { enabled, code: enabled ? code : null });
      toast.success('Welcome offer settings saved');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const saveSystem = async () => {
    setSaving(true);
    try {
      await client.put('/admin/settings/system', {
        delivery_fee: parseFloat(deliveryFee),
        min_order_value: parseFloat(minOrderValue),
        free_delivery_threshold: parseFloat(freeDeliveryThreshold),
        tax_percentage: parseFloat(taxPercentage),
        show_tax_and_delivery: showTaxAndDelivery,
        is_online: isOnline,
        allow_preorders: allowPreorders,
        operating_hours: operatingHours,
        enable_todays_special: enableTodaysSpecial,
        todays_special_title: todaysSpecialTitle,
        accept_cod: acceptCod,
        accept_cashfree: acceptCashfree
      });
      toast.success('System configurations saved successfully!');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const updateDaySchedule = (day, field, value) => {
    setOperatingHours(prev => {
      const currentDayConfig = prev[day] || { is_open: false, start_time: '09:00', end_time: '22:00' };
      return {
        ...prev,
        [day]: {
          ...currentDayConfig,
          [field]: value
        }
      };
    });
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-saffron" /></div>;

  return (
    <div className="max-w-4xl font-body">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-textprimary">Settings</h1>
        <p className="text-textsecondary text-sm">Manage business operation rules, operating hours, delivery fees, and taxes</p>
      </div>

      {/* Tabs navigation */}
      <div className="flex bg-bgsecondary p-1 rounded-2xl mb-6 max-w-md font-heading">
        <button
          type="button"
          onClick={() => setActiveTab('system')}
          className={`flex-1 py-2 text-sm font-semibold rounded-xl transition-all ${
            activeTab === 'system'
              ? 'bg-white text-maroon shadow-sm'
              : 'text-textsecondary hover:text-textprimary'
          }`}
        >
          Kitchen Operations
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('coupons')}
          className={`flex-1 py-2 text-sm font-semibold rounded-xl transition-all ${
            activeTab === 'coupons'
              ? 'bg-white text-maroon shadow-sm'
              : 'text-textsecondary hover:text-textprimary'
          }`}
        >
          Welcome Offer
        </button>
      </div>

      {/* SYSTEM SETTINGS TAB */}
      {activeTab === 'system' && (
        <div className="space-y-6">
          {/* Main operations card */}
          <div className="bg-white rounded-2xl p-6 shadow-card border border-borderbeige">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-maroon" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-textprimary">Kitchen Storefront Status</h2>
                <p className="text-xs text-textsecondary">Manually override online status and configure pre-ordering fallback rules</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center justify-between p-4 bg-bgsecondary rounded-2xl cursor-pointer">
                <div>
                  <div className="font-heading font-semibold text-sm text-textprimary flex items-center gap-1.5">
                    Storefront Online
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                  </div>
                  <div className="text-xs text-textsecondary mt-0.5">Toggle to manually go online/offline</div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOnline(!isOnline)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${isOnline ? 'bg-green-500' : 'bg-textlight'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isOnline ? 'translate-x-6' : ''}`} />
                </button>
              </label>

              <label className="flex items-center justify-between p-4 bg-bgsecondary rounded-2xl cursor-pointer">
                <div>
                  <div className="font-heading font-semibold text-sm text-textprimary flex items-center gap-1.5">
                    Show Today's Special Section
                    <span className="text-amber-500">⭐</span>
                  </div>
                  <div className="text-xs text-textsecondary mt-0.5">Show featured Today's Special dishes on homepage</div>
                </div>
                <button
                  type="button"
                  onClick={() => setEnableTodaysSpecial(!enableTodaysSpecial)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${enableTodaysSpecial ? 'bg-amber-500' : 'bg-textlight'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${enableTodaysSpecial ? 'translate-x-6' : ''}`} />
                </button>
              </label>

              <label className="flex items-center justify-between p-4 bg-bgsecondary rounded-2xl cursor-pointer">
                <div>
                  <div className="font-heading font-semibold text-sm text-textprimary">Allow Pre-orders when Closed</div>
                  <div className="text-xs text-textsecondary mt-0.5">Allow checkout in closed hours with forced delivery slot</div>
                </div>
                <button
                  type="button"
                  onClick={() => setAllowPreorders(!allowPreorders)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${allowPreorders ? 'bg-maroon' : 'bg-textlight'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${allowPreorders ? 'translate-x-6' : ''}`} />
                </button>
              </label>

              <label className="flex items-center justify-between p-4 bg-bgsecondary rounded-2xl cursor-pointer">
                <div>
                  <div className="font-heading font-semibold text-sm text-textprimary">Enable Cash on Delivery (COD)</div>
                  <div className="text-xs text-textsecondary mt-0.5">Allow users to choose COD at checkout</div>
                </div>
                <button
                  type="button"
                  onClick={() => setAcceptCod(!acceptCod)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${acceptCod ? 'bg-maroon' : 'bg-textlight'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${acceptCod ? 'translate-x-6' : ''}`} />
                </button>
              </label>

              <label className="flex items-center justify-between p-4 bg-bgsecondary rounded-2xl cursor-pointer md:col-span-2">
                <div>
                  <div className="font-heading font-semibold text-sm text-textprimary">Enable UPI / Card (Cashfree)</div>
                  <div className="text-xs text-textsecondary mt-0.5">Allow online payment with Cashfree at checkout</div>
                </div>
                <button
                  type="button"
                  onClick={() => setAcceptCashfree(!acceptCashfree)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${acceptCashfree ? 'bg-maroon' : 'bg-textlight'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${acceptCashfree ? 'translate-x-6' : ''}`} />
                </button>
              </label>
            </div>
          </div>

          {/* Rates and taxes card */}
          <div className="bg-white rounded-2xl p-6 shadow-card border border-borderbeige">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-textprimary">Billing & Rates Config</h2>
                <p className="text-xs text-textsecondary">Set delivery fees, tax percentages, and minimum order validation thresholds</p>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-center justify-between p-4 bg-bgsecondary rounded-2xl cursor-pointer">
                <div>
                  <div className="font-heading font-semibold text-sm text-textprimary">Apply Rates to Users</div>
                  <div className="text-xs text-textsecondary mt-0.5">Toggle whether taxes &amp; delivery fees are applied on checkout</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTaxAndDelivery(!showTaxAndDelivery)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${showTaxAndDelivery ? 'bg-saffron' : 'bg-textlight'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${showTaxAndDelivery ? 'translate-x-6' : ''}`} />
                </button>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-textprimary font-heading font-semibold mb-1 block">Delivery Fee (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={deliveryFee}
                    onChange={(e) => setDeliveryFee(e.target.value)}
                    disabled={!showTaxAndDelivery}
                    className="w-full px-3 py-2 border border-borderbeige rounded-xl text-sm bg-white disabled:bg-bgsecondary disabled:cursor-not-allowed focus:outline-none focus:border-saffron"
                  />
                </div>
                <div>
                  <label className="text-xs text-textprimary font-heading font-semibold mb-1 block">Free Delivery Min (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={freeDeliveryThreshold}
                    onChange={(e) => setFreeDeliveryThreshold(e.target.value)}
                    disabled={!showTaxAndDelivery}
                    className="w-full px-3 py-2 border border-borderbeige rounded-xl text-sm bg-white disabled:bg-bgsecondary disabled:cursor-not-allowed focus:outline-none focus:border-saffron"
                  />
                </div>
                <div>
                  <label className="text-xs text-textprimary font-heading font-semibold mb-1 block">Min. Order for Checkout (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={minOrderValue}
                    onChange={(e) => setMinOrderValue(e.target.value)}
                    className="w-full px-3 py-2 border border-borderbeige rounded-xl text-sm bg-white focus:outline-none focus:border-saffron"
                  />
                </div>
                <div>
                  <label className="text-xs text-textprimary font-heading font-semibold mb-1 block">Tax Percentage (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={taxPercentage}
                    onChange={(e) => setTaxPercentage(e.target.value)}
                    disabled={!showTaxAndDelivery}
                    className="w-full px-3 py-2 border border-borderbeige rounded-xl text-sm bg-white disabled:bg-bgsecondary disabled:cursor-not-allowed focus:outline-none focus:border-saffron"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Operating hours schedule card */}
          <div className="bg-white rounded-2xl p-6 shadow-card border border-borderbeige">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-saffron" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-textprimary">Weekly Operating Hours</h2>
                <p className="text-xs text-textsecondary">Select days the kitchen is open and set operating hours (24hr clock format)</p>
              </div>
            </div>

            <div className="divide-y divide-borderbeige">
              {daysOrdered.map(day => {
                const dayConfig = operatingHours[day] || { is_open: false, start_time: '09:00', end_time: '22:00' };
                return (
                  <div key={day} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="w-32 font-heading font-bold text-sm text-textprimary capitalize">
                      {day}
                    </div>

                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 text-xs text-textprimary font-semibold cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dayConfig.is_open}
                          onChange={(e) => updateDaySchedule(day, 'is_open', e.target.checked)}
                          className="w-4 h-4 rounded text-maroon focus:ring-maroon border-borderbeige"
                        />
                        Open for Delivery
                      </label>

                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={dayConfig.start_time || '09:00'}
                          onChange={(e) => updateDaySchedule(day, 'start_time', e.target.value)}
                          disabled={!dayConfig.is_open}
                          className="px-2 py-1 text-xs border border-borderbeige rounded-lg focus:outline-none focus:border-saffron disabled:bg-bgsecondary"
                        />
                        <span className="text-textsecondary text-xs">to</span>
                        <input
                          type="time"
                          value={dayConfig.end_time || '22:00'}
                          onChange={(e) => updateDaySchedule(day, 'end_time', e.target.value)}
                          disabled={!dayConfig.is_open}
                          className="px-2 py-1 text-xs border border-borderbeige rounded-lg focus:outline-none focus:border-saffron disabled:bg-bgsecondary"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-borderbeige">
              <button
                type="button"
                onClick={saveSystem}
                disabled={saving}
                className="w-full sm:w-auto px-6 py-3 bg-maroon hover:bg-maroon-dark text-white rounded-2xl font-heading font-bold shadow-warm flex items-center justify-center gap-2 cursor-pointer transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Kitchen Configurations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WELCOME COUPON TAB */}
      {activeTab === 'coupons' && (
        <div className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-offer flex items-center justify-center"><Gift className="w-5 h-5 text-maroon" /></div>
            <div>
              <h2 className="font-heading font-bold text-lg text-textprimary">Welcome Coupon</h2>
              <p className="text-xs text-textsecondary font-body">Auto-apply a coupon for first-time users after onboarding</p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between p-3 bg-bgsecondary rounded-xl cursor-pointer">
              <div>
                <div className="font-heading font-semibold text-sm text-textprimary">Enable welcome offer</div>
                <div className="text-xs text-textsecondary font-body">Applied automatically to next order</div>
              </div>
              <button
                type="button"
                onClick={() => setEnabled(!enabled)}
                data-testid="admin-welcome-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-saffron' : 'bg-textlight'}`}
                aria-pressed={enabled}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : ''}`} />
              </button>
            </label>

            <div>
              <label className="text-xs text-textprimary font-heading font-semibold mb-1 block">Select coupon code</label>
              <select
                data-testid="admin-welcome-code-select"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={!enabled}
                className="w-full px-3 py-2.5 border border-borderbeige rounded-xl font-body text-sm bg-white disabled:bg-bgsecondary disabled:cursor-not-allowed"
              >
                <option value="">— Select from active coupons —</option>
                {coupons.filter((c) => c.is_active).map((c) => (
                  <option key={c.id} value={c.code}>
                    {c.code} — {c.type === 'percent' ? `${c.value}% off` : `₹${c.value} off`}
                    {c.min_order > 0 ? ` (min ₹${c.min_order})` : ''}
                  </option>
                ))}
              </select>
              {coupons.length === 0 && <p className="text-xs text-maroon mt-1 font-body">No active coupons available. Create one under Coupons.</p>}
            </div>

            <button
              onClick={saveWelcome}
              disabled={saving}
              data-testid="admin-welcome-save"
              className="w-full bg-saffron text-white py-2.5 rounded-2xl font-heading font-bold shadow-warm disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Welcome Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

